import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, FEATURE_LINKS } from "../lib/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const location = useLocation();
  const mainLinks = useMemo(() => NAV_LINKS.filter((l) => l.label !== "Features"), []); // NAV_LINKS is a stable module constant

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setFeaturesOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled || open ? "glass-strong" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 md:px-10">
        <Logo />

        <div className="hidden items-center gap-1 lg:flex">
          {mainLinks.map((link) => (
            <NavItem key={link.to} link={link} active={location.pathname === link.to} />
          ))}
          <div
            className="relative"
            onMouseEnter={() => setFeaturesOpen(true)}
            onMouseLeave={() => setFeaturesOpen(false)}
          >
            <Link
              to="/features"
              data-testid="nav-features"
              className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                location.pathname.startsWith("/features") ? "text-white" : "text-zinc-400 hover:text-white"
              }`}
            >
              Features <ChevronDown className="h-3.5 w-3.5" />
            </Link>
            {featuresOpen && (
              <div className="absolute left-1/2 top-full w-64 -translate-x-1/2 pt-3">
                <div className="glass-strong rounded-2xl p-2 shadow-2xl">
                  {[{ label: "All Features", to: "/features" }, ...FEATURE_LINKS].map((f) => (
                    <Link
                      key={f.to + f.label}
                      to={f.to}
                      data-testid={`nav-dropdown-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="block rounded-xl px-4 py-2.5 text-sm text-zinc-300 transition-colors hover:bg-white/5 hover:text-white"
                    >
                      {f.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <Link to="/business-partnerships" data-testid="nav-cta-partnerships" className="btn-gold !px-5 !py-2.5">
            Partner With Us
          </Link>
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {open && (
        <div className="border-t border-white/10 lg:hidden" data-testid="mobile-menu">
          <div className="mx-auto max-w-7xl px-6 py-6">
            {[...NAV_LINKS, ...FEATURE_LINKS].map((link) => (
              <Link
                key={link.to + link.label}
                to={link.to}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="block border-b border-white/5 py-3.5 text-base text-zinc-300 transition-colors hover:text-white"
              >
                {link.label}
              </Link>
            ))}
            <Link to="/business-partnerships" className="btn-gold mt-5 w-full">
              Partner With Us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

const NavItem = ({ link, active }) => (
  <Link
    to={link.to}
    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
    className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
      active ? "text-white" : "text-zinc-400 hover:text-white"
    }`}
  >
    {link.label}
  </Link>
);
